import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';

import { faqBlockHtml } from '../../footer-html';
import { FOOTER_HREF_RE, footerEditorExtensions } from './extensions';
import {
  Content,
  Frame,
  LinkAction,
  LinkError,
  LinkInput,
  LinkRow,
  Root,
  TextTool,
  ToolButton,
  ToolGroup,
  Toolbar,
} from './FooterTextEditor.styles';

export type FooterTextEditorProps = {
  /** The text's HTML. Read at mount; a later value the editor did not emit itself (a re-read) replaces the content. */
  html: string;
  /** Fired with `getHTML()` on every change the owner makes. */
  onChange: (html: string) => void;
  /** Shows the text only: no toolbar, no typing. */
  readOnly?: boolean;
  /** Something is being written into the text: the toolbar goes inert and typing stops. */
  busy?: boolean;
  /** Told after a "Question and answer" block went in. */
  onInsertQa?: () => void;
  /** Focus the text at mount (a "Write a text" click opens the editor ready to type). */
  focusOnMount?: boolean;
  /** The accessible name of the text and its toolbar (already translated). */
  label: string;
};

/** The position just inside the first empty H3 at or after `from`: where a fresh Q&A block's question goes. */
const emptyQuestionAfter = (editor: Editor, from: number): number | null => {
  let found: number | null = null;
  const { doc } = editor.state;
  doc.nodesBetween(Math.max(0, from - 1), doc.content.size, (node, pos) => {
    if (found !== null) return false;
    if (node.type.name === 'heading' && node.attrs.level === 3 && node.content.size === 0) {
      found = pos + 1;
      return false;
    }
    return true;
  });
  return found;
};

/**
 * The footer SEO text editor: H2, H3, bold, italic, lists, links, and a "Question and answer" block (an H3 question
 * with its answer paragraph). The schema is the server's allowlist, so pasted markup outside it never reaches the
 * HTML. The caller keys it by (path, language), so a selection change starts a fresh editor.
 */
export const FooterTextEditor = ({
  html,
  onChange,
  readOnly = false,
  busy = false,
  onInsertQa,
  focusOnMount = false,
  label,
}: FooterTextEditorProps): React.ReactElement => {
  const { t } = useTranslation();
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkRefused, setLinkRefused] = React.useState(false);
  const linkInputRef = React.useRef<HTMLInputElement>(null);
  // The editor is created once; its callbacks read the latest props through refs.
  const onChangeRef = React.useRef(onChange);
  const emittedRef = React.useRef(html);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editable = !readOnly && !busy;
  const editor = useEditor({
    extensions: footerEditorExtensions(),
    content: html,
    editable,
    autofocus: focusOnMount && editable ? 'end' : false,
    editorProps: { attributes: { 'aria-label': label, role: 'textbox', 'aria-multiline': 'true' } },
    onUpdate: ({ editor: current }) => {
      const next = current.getHTML();
      emittedRef.current = next;
      onChangeRef.current(next);
    },
  });

  // Syncs the ProseMirror view with the props: editability, and a text that changed outside the editor.
  React.useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editor, editable]);
  React.useEffect(() => {
    if (!editor || html === emittedRef.current) return;
    emittedRef.current = html;
    if (html !== editor.getHTML()) editor.commands.setContent(html, false);
  }, [editor, html]);

  // Moves the DOM focus into the link row when the link tool opens it.
  React.useEffect(() => {
    if (linkOpen) linkInputRef.current?.focus();
  }, [linkOpen]);

  if (!editor) return <Root />;

  const applyLink = (): void => {
    if (!editable) return;
    const url = linkUrl.trim();
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (!FOOTER_HREF_RE.test(url)) {
      setLinkRefused(true);
      return;
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkOpen(false);
    setLinkUrl('');
    setLinkRefused(false);
  };

  const toggleLink = (): void => {
    if (!editable) return;
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    setLinkRefused(false);
    setLinkOpen((open) => !open);
  };

  const insertQa = (): void => {
    if (!editable) return;
    const { from } = editor.state.selection;
    editor.chain().focus().insertContent(faqBlockHtml('', '')).run();
    const question = emptyQuestionAfter(editor, from);
    if (question !== null) editor.chain().focus().setTextSelection(question).run();
    onInsertQa?.();
  };

  /** One toolbar button: its name is both the accessible name and the tooltip. */
  const tool = (name: string, icon: string, onClick: () => void, pressed: boolean): React.ReactElement => (
    <ToolButton
      key={name}
      type="button"
      aria-label={name}
      title={name}
      aria-pressed={pressed}
      $active={pressed}
      disabled={!editable}
      onClick={onClick}
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
    </ToolButton>
  );

  return (
    <Root>
      <Frame $readOnly={readOnly}>
        {!readOnly ? (
          <Toolbar role="toolbar" aria-label={label}>
            <ToolGroup>
              {tool(
                t('Section heading'),
                'bi-type-h2',
                () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
                editor.isActive('heading', { level: 2 }),
              )}
              {tool(
                t('Subheading'),
                'bi-type-h3',
                () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
                editor.isActive('heading', { level: 3 }),
              )}
            </ToolGroup>
            <ToolGroup>
              {tool(
                t('Bold'),
                'bi-type-bold',
                () => editor.chain().focus().toggleBold().run(),
                editor.isActive('bold'),
              )}
              {tool(
                t('Italic'),
                'bi-type-italic',
                () => editor.chain().focus().toggleItalic().run(),
                editor.isActive('italic'),
              )}
            </ToolGroup>
            <ToolGroup>
              {tool(
                t('Bullet list'),
                'bi-list-ul',
                () => editor.chain().focus().toggleBulletList().run(),
                editor.isActive('bulletList'),
              )}
              {tool(
                t('Numbered list'),
                'bi-list-ol',
                () => editor.chain().focus().toggleOrderedList().run(),
                editor.isActive('orderedList'),
              )}
              {tool(t('Link'), 'bi-link-45deg', toggleLink, editor.isActive('link'))}
            </ToolGroup>
            <ToolGroup>
              <TextTool type="button" disabled={!editable} onClick={insertQa}>
                <i className="bi bi-patch-question" aria-hidden="true" />
                {t('Question and answer')}
              </TextTool>
            </ToolGroup>
          </Toolbar>
        ) : null}
        {!readOnly && linkOpen ? (
          <>
            <LinkRow>
              <LinkInput
                ref={linkInputRef}
                value={linkUrl}
                placeholder={t('https://… or /slots')}
                aria-label={t('Link address')}
                onChange={(event) => {
                  setLinkUrl(event.target.value);
                  setLinkRefused(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyLink();
                  }
                }}
              />
              <LinkAction type="button" onClick={applyLink}>
                {t('Apply')}
              </LinkAction>
            </LinkRow>
            {linkRefused ? <LinkError text="Use an https:// address or a path that starts with /" tag="span" /> : null}
          </>
        ) : null}
        <Content $readOnly={readOnly}>
          <EditorContent editor={editor} />
        </Content>
      </Frame>
    </Root>
  );
};

export default FooterTextEditor;
